/**
 * Pure geometry and snap logic for the generic bottom panel resize module.
 *
 * @remarks
 * No React, DOM mutation, or itinerary-specific business rules. Unit-tested in isolation.
 */

import { ITINERARY_PANEL } from "../../../config/itineraryPanel.configuration";
import type { PanelDragBounds, PanelResizeProfile, PanelSnapAnchorsVh, PanelSnapTier } from "./panelResize.types";

/** Default DOM id of the FAB row used to cap the maximized panel height. */
export const DEFAULT_FLOATING_ACTION_ROW_ELEMENT_ID = "FloatingMenuFabRow";

/**
 * Resolves the browser viewport height used for px ↔ vh conversions.
 *
 * @returns Viewport height in CSS pixels (`visualViewport` when available).
 */
export const resolveViewportHeightPx = (): number => {
    if ( typeof window === "undefined" ) {
        return 0;
    }

    return window.visualViewport?.height ?? window.innerHeight;
};

/**
 * Converts a pixel distance to viewport height units.
 *
 * @param px - Distance in CSS pixels.
 * @param viewportHeightPx - Viewport height in CSS pixels.
 * @returns Equivalent distance in vh units.
 */
export const pxToVh = ( px: number, viewportHeightPx: number ): number => {
    if ( viewportHeightPx <= 0 ) {
        return 0;
    }

    return ( px * 100 ) / viewportHeightPx;
};

/**
 * Clamps a panel height (vh) between a minimum and maximum bound.
 *
 * @param heightVh - Candidate height in vh.
 * @param minVh - Minimum allowed height in vh.
 * @param maxVh - Maximum allowed height in vh.
 * @returns `heightVh` clamped to `[minVh, maxVh]`.
 */
export const clampPanelHeightVh = ( heightVh: number, minVh: number, maxVh: number ): number => {
    return Math.min( maxVh, Math.max( minVh, heightVh ) );
};

/**
 * Resolves the live drag floor (vh) from a profile's fixed chrome row height.
 *
 * @param profile - Surface resize profile supplying `chromeRowHeightPx`.
 * @param viewportHeightPx - Current viewport height in pixels.
 * @returns Minimum live drag height in vh (not the collapsed snap anchor).
 */
export const resolveChromeMinHeightVh = ( profile: PanelResizeProfile, viewportHeightPx: number ): number => {
    if ( viewportHeightPx <= 0 ) {
        return 0;
    }

    return pxToVh( profile.chromeRowHeightPx, viewportHeightPx );
};

/**
 * Measures the rendered height of the panel container and converts it to vh.
 *
 * @param element - Panel container element, or `null` if not yet mounted.
 * @param viewportHeightPx - Current viewport height in pixels.
 * @returns Rendered height in vh, or `null` when it cannot be measured.
 */
export const measureRenderedPanelHeightVh = ( element: HTMLElement | null, viewportHeightPx: number ): number | null => {
    if ( element === null || viewportHeightPx <= 0 ) {
        return null;
    }

    const renderedHeightPx = element.getBoundingClientRect().height;
    if ( renderedHeightPx <= 0 ) {
        return null;
    }

    return pxToVh( renderedHeightPx, viewportHeightPx );
};

/**
 * Measures the lowest bottom edge (viewport px) of the floating action row (GPS + grouped FAB).
 *
 * @param fabRowElementId - DOM id of the FAB row container.
 * @returns Bottom Y in viewport coordinates, or `null` when the row is not mounted.
 */
export const measureFloatingActionRowBottomPx = ( fabRowElementId: string = DEFAULT_FLOATING_ACTION_ROW_ELEMENT_ID ): number | null => {
    if ( typeof document === "undefined" ) {
        return null;
    }

    const fabRowElement = document.getElementById( fabRowElementId );
    if ( fabRowElement === null ) {
        return null;
    }

    const rowRect = fabRowElement.getBoundingClientRect();
    if ( rowRect.height <= 0 ) {
        return null;
    }

    let lowestBottomPx = rowRect.bottom;
    const interactiveControls = fabRowElement.querySelectorAll( "button, [role=\"button\"]" );

    for ( const control of interactiveControls ) {
        if ( control instanceof HTMLElement ) {
            lowestBottomPx = Math.max( lowestBottomPx, control.getBoundingClientRect().bottom );
        }
    }

    return lowestBottomPx;
};

/**
 * Computes the dynamic `maximized` anchor: available height (vh) below the FAB row.
 *
 * @param fabRowBottomPx - Bottom Y (viewport px) of the FAB row, or `null` when unmeasured.
 * @param viewportHeightPx - Current viewport height in pixels.
 * @param panelTopGapPx - Breathing room (px) between the FAB row and the maximized panel top.
 * @param fallbackMaxVh - Graceful fallback when measurement is unavailable.
 * @param panelBottomPx - Bottom Y (viewport px) of the panel anchor, or `null` to use the viewport bottom.
 * @returns Available maximized height in vh, capped below the floating action chrome.
 */
export const measureMaximizedPanelHeightVh = (
    fabRowBottomPx: number | null,
    viewportHeightPx: number,
    panelTopGapPx: number,
    fallbackMaxVh: number = ITINERARY_PANEL.panelMaxHeightVh,
    panelBottomPx: number | null = null,
): number => {
    if ( fabRowBottomPx === null || viewportHeightPx <= 0 ) {
        return fallbackMaxVh;
    }

    const anchorBottomPx = panelBottomPx ?? viewportHeightPx;
    const availablePx = anchorBottomPx - fabRowBottomPx - panelTopGapPx;
    if ( availablePx <= 0 ) {
        return fallbackMaxVh;
    }

    const measuredMaxVh = pxToVh( availablePx, viewportHeightPx );

    return measuredMaxVh;
};

/**
 * Resolves the height (vh) a drag gesture should start from.
 *
 * @remarks
 * Prefers the rendered height over the committed value to avoid jumps when a snap
 * transition is still animating. Only clamped by the dynamic upper bound, not the
 * collapsed snap anchor.
 *
 * @param params.committedHeightVh - Last committed snap height (vh).
 * @param params.renderedHeightVh - Measured rendered height (vh), or `null`.
 * @param params.maxVh - Upper bound (vh) for the drag range (maximized anchor).
 * @returns Height (vh) to use as the drag baseline.
 */
export const resolveDragStartHeightVh = ( params: {
    "committedHeightVh": number;
    "renderedHeightVh":  number | null;
    "maxVh":             number;
} ): number => {
    const { committedHeightVh, renderedHeightVh, maxVh } = params;

    /*
     * Always seed from what is actually painted so pointer down does not jump to a
     * committed snap box that is taller than the collapsed chrome-only layout.
     */
    const baseline = renderedHeightVh ?? committedHeightVh;

    return Math.min( baseline, maxVh );
};

/**
 * Computes live panel height (vh) from the pointer position and grab offset on the panel top.
 *
 * @remarks
 * Bottom-anchored panel: the bottom edge stays fixed while the top follows the pointer,
 * preserving the grab point under the finger/cursor (no delta drift after a mismatched seed).
 *
 * @param pointerClientY - Current pointer Y in viewport coordinates.
 * @param pointerOffsetFromPanelTopPx - `pointerdown` Y minus panel top (px).
 * @param panelBottomPx - Panel bottom edge Y in viewport coordinates.
 * @param viewportHeightPx - Current viewport height in pixels.
 * @param bounds - Live drag clamp bounds.
 * @returns Clamped panel height (vh) for the current pointer position.
 */
export const computeDragHeightFromPointerGrab = (
    pointerClientY: number,
    pointerOffsetFromPanelTopPx: number,
    panelBottomPx: number,
    viewportHeightPx: number,
    bounds: PanelDragBounds,
): number => {
    const nextHeightPx = panelBottomPx - ( pointerClientY - pointerOffsetFromPanelTopPx );
    const nextHeightVh = pxToVh( Math.max( 0, nextHeightPx ), viewportHeightPx );

    return clampPanelHeightVh(
        nextHeightVh,
        bounds.chromeMinHeightVh,
        bounds.maximizedHeightVh,
    );
};

/**
 * Resolves whether itinerary leg details should be visible during an active resize gesture.
 *
 * @remarks
 * While dragging, leg details stay mounted until the live height drops to the chrome row floor
 * (not the collapsed snap anchor), so content is clipped by the panel instead of unmounting early.
 *
 * @param panelSnapTier - Last committed snap tier.
 * @param dragHeightVh - Live drag height (vh), or `null` when idle.
 * @param chromeMinHeightVh - Live drag floor (vh) from the profile chrome row height.
 * @returns `true` when leg details should render.
 */
export const resolveLegDetailsVisibleDuringResize = (
    panelSnapTier: PanelSnapTier,
    dragHeightVh: number | null,
    chromeMinHeightVh: number,
): boolean => {
    if ( dragHeightVh !== null ) {
        return dragHeightVh > chromeMinHeightVh;
    }

    return panelSnapTier !== "collapsed";
};

/**
 * Builds snap anchor heights for a profile and the current maximized measurement.
 *
 * @param profile - Surface resize profile.
 * @param maximizedHeightVh - Dynamic maximized anchor (vh).
 * @returns Snap anchors for nearest-tier resolution.
 */
export const buildSnapAnchors = ( profile: PanelResizeProfile, maximizedHeightVh: number ): PanelSnapAnchorsVh => {
    return {
        "collapsedVh": profile.snapCollapsedVh,
        "expandedVh":  profile.snapExpandedVh,
        "maximizedVh": maximizedHeightVh,
    };
};

/**
 * Builds live drag clamp bounds for pointer tracking.
 *
 * @param profile - Surface resize profile.
 * @param maximizedHeightVh - Dynamic upper bound (vh).
 * @param viewportHeightPx - Current viewport height in pixels.
 * @returns Live drag bounds (`chromeMin`..`maximized`).
 */
export const buildPanelDragBounds = (
    profile: PanelResizeProfile,
    maximizedHeightVh: number,
    viewportHeightPx: number,
): PanelDragBounds => {
    const anchors = buildSnapAnchors( profile, maximizedHeightVh );

    return {
        "chromeMinHeightVh": resolveProfileMinDragHeightVh( profile, anchors, viewportHeightPx ),
        "maximizedHeightVh": maximizedHeightVh,
    };
};

/**
 * Computes the live panel height (vh) from pointer movement.
 *
 * @param initialHeightVh - Height (vh) recorded at `pointerdown`.
 * @param deltaPx - Pointer Y delta since `pointerdown` (positive = moved down).
 * @param viewportHeightPx - Current viewport height in pixels.
 * @param bounds - Live drag clamp bounds.
 * @returns Clamped panel height (vh) for the current pointer position.
 */
export const computeDragHeightFromPointer = (
    initialHeightVh: number,
    deltaPx: number,
    viewportHeightPx: number,
    bounds: PanelDragBounds,
): number => {
    const deltaVh = pxToVh( deltaPx, viewportHeightPx );

    return clampPanelHeightVh(
        initialHeightVh - deltaVh,
        bounds.chromeMinHeightVh,
        bounds.maximizedHeightVh,
    );
};

/**
 * Picks the snap tier whose anchor is closest to the current height.
 *
 * @param currentHeightVh - Current live panel height in vh.
 * @param anchors - Snap anchor heights (vh).
 * @param allowedSnapTiers - Tiers this surface may commit to.
 * @returns Nearest allowed {@link PanelSnapTier}.
 */
export const resolveNearestSnapTier = (
    currentHeightVh: number,
    anchors: PanelSnapAnchorsVh,
    allowedSnapTiers: readonly PanelSnapTier[],
): PanelSnapTier => {
    const candidates = allowedSnapTiers.map( ( tier ): [PanelSnapTier, number] => [
        tier,
        Math.abs( currentHeightVh - resolveSnapTierHeightVh( tier, anchors ) ),
    ] );

    if ( candidates.length === 0 ) {
        return "expanded";
    }

    return candidates.reduce( ( nearest, candidate ) => ( candidate[1] < nearest[1] ? candidate : nearest ) )[0];
};

/**
 * Returns the lowest snap tier allowed for a surface (keyboard / shrink default).
 *
 * @param allowedSnapTiers - Tiers this surface may commit to.
 * @returns `collapsed` when allowed, otherwise `expanded`.
 */
export const resolveLowestAllowedSnapTier = ( allowedSnapTiers: readonly PanelSnapTier[] ): PanelSnapTier => {
    if ( allowedSnapTiers.includes( "collapsed" ) ) {
        return "collapsed";
    }

    return "expanded";
};

/**
 * Resolves the live drag floor (vh) for a profile, respecting allowed snap tiers.
 *
 * @param profile - Surface resize profile.
 * @param anchors - Snap anchor heights (vh).
 * @param viewportHeightPx - Current viewport height in pixels.
 * @returns Minimum live drag height in vh.
 */
export const resolveProfileMinDragHeightVh = (
    profile: PanelResizeProfile,
    anchors: PanelSnapAnchorsVh,
    viewportHeightPx: number,
): number => {
    const chromeMinHeightVh = resolveChromeMinHeightVh( profile, viewportHeightPx );

    if ( profile.allowedSnapTiers.includes( "collapsed" ) ) {
        return chromeMinHeightVh;
    }

    return Math.max( chromeMinHeightVh, anchors.expandedVh );
};

/**
 * Resolves the committed height (vh) for a given snap tier.
 *
 * @param tier - Snap tier to resolve.
 * @param anchors - Snap anchor heights (vh).
 * @returns Height (vh) associated with `tier`.
 */
export const resolveSnapTierHeightVh = ( tier: PanelSnapTier, anchors: PanelSnapAnchorsVh ): number => {
    switch ( tier ) {
        case "collapsed":  return anchors.collapsedVh;

        case "expanded":   return anchors.expandedVh;

        case "maximized":  return anchors.maximizedVh;
    }
};

/**
 * Decides whether a live drag height update is negligible and should be skipped.
 *
 * @param previousHeightVh - Last applied height (vh), or `null` if none yet.
 * @param nextHeightVh - Candidate next height (vh).
 * @param epsilonVh - Minimum meaningful change (vh).
 * @returns `true` when the update should be skipped.
 */
export const shouldSkipDragHeightUpdate = (
    previousHeightVh: number | null,
    nextHeightVh: number,
    epsilonVh: number,
): boolean => {
    if ( previousHeightVh === null ) {
        return false;
    }

    return Math.abs( previousHeightVh - nextHeightVh ) < epsilonVh;
};
